import hkLogo from "@repo/assets/logos/hk-black.png"
import { Avatar, AvatarFallback } from "@repo/ui/components/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/dropdown-menu"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@repo/ui/components/sidebar"
import { ChevronsUpDown, LogOut, UserRound } from "lucide-react"
import { Link, useLocation, useNavigate } from "react-router"

import { authClient } from "@/lib/auth-client"
import { initialsFor } from "@/lib/initials"
import { isNavActive, navGroups } from "@/lib/nav"
import { useBranding } from "@/lib/use-branding"

export function AppSidebar({
  user,
}: {
  user: { name?: string | null; email: string }
}) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const displayName = user.name ?? "Henry Klevor"
  const branding = useBranding()
  const logoLight = branding?.logoLight
  const logoDark = branding?.logoDark

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader className="h-16 justify-center px-3">
        <Link
          to="/"
          aria-label="Henry Klevor dashboard"
          className="flex items-center"
        >
          <img
            src={logoLight?.url ?? hkLogo}
            alt={logoLight?.alt ?? "Henry Klevor"}
            className={
              logoDark
                ? "h-8 w-auto group-data-[collapsible=icon]:hidden dark:hidden"
                : "h-8 w-auto group-data-[collapsible=icon]:hidden dark:invert"
            }
          />
          {logoDark && (
            <img
              src={logoDark.url}
              alt={logoDark.alt ?? "Henry Klevor"}
              className="hidden h-8 w-auto group-data-[collapsible=icon]:hidden dark:block"
            />
          )}
          <span className="hidden size-8 items-center justify-center rounded-md bg-sidebar-primary font-sans text-xs font-bold text-sidebar-primary-foreground group-data-[collapsible=icon]:flex">
            HK
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.title}>
            <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      render={<Link to={item.to} />}
                      isActive={isNavActive(pathname, item.to)}
                      tooltip={item.title}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={<SidebarMenuButton size="lg" tooltip={displayName} />}
              >
                <Avatar className="size-8 shrink-0 rounded-lg">
                  <AvatarFallback className="rounded-lg bg-sidebar-primary text-xs font-semibold text-sidebar-primary-foreground">
                    {initialsFor(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid min-w-0 flex-1 text-left leading-tight">
                  <span className="truncate text-sm font-medium">
                    {displayName}
                  </span>
                  <span className="truncate text-xs text-sidebar-foreground/60">
                    {user.email}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="start"
                className="min-w-56 rounded-lg"
              >
                <DropdownMenuItem onClick={() => void navigate("/profile")}>
                  <UserRound />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => authClient.signOut()}>
                  <LogOut />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
